import { Injectable, computed, inject, signal } from '@angular/core';
import { filter, take } from 'rxjs/operators';

import { AuthService } from './auth.service';
import { GraphqlService, OrganizacionDelUsuario } from '../services/graphql.service';

export type EstadoOrganizaciones = 'sin-cargar' | 'cargando' | 'ok' | 'error';

/**
 * Las organizaciones del usuario y el cambio entre ellas: lo que hay detrás del selector de
 * organización.
 *
 * La lista sale de la api (`getMyOrganizations`), que ya la cruza con los tenants
 * desplegados en este entorno. Se pide UNA vez por carga de página, cuando el token ha
 * confirmado el tenant: cambiar de organización recarga la app entera, así que no hay nada
 * que refrescar en caliente.
 */
@Injectable({ providedIn: 'root' })
export class OrganizacionesService {
  private readonly auth = inject(AuthService);
  private readonly gql = inject(GraphqlService);

  readonly estado = signal<EstadoOrganizaciones>('sin-cargar');
  readonly organizaciones = signal<OrganizacionDelUsuario[]>([]);
  /** A dónde se está cambiando (`'auth0'` = a su selector). Bloquea un segundo cambio
   *  mientras sale la redirección. */
  readonly cambiandoA = signal<OrganizacionDelUsuario | 'auth0' | null>(null);

  /** La organización del token, si está en la lista. */
  readonly actual = computed(() => this.organizaciones().find(o => o.current) ?? null);
  /** Hay otra a la que ir. */
  readonly hayOtras = computed(() => this.organizaciones().some(o => !o.current));

  private pedida = false;

  /** Pide la lista en cuanto el tenant esté confirmado. Llamarla más veces no repite. */
  cargar(): void {
    if (this.pedida) return;
    this.pedida = true;
    this.auth.estadoTenant$.pipe(filter(estado => estado === 'ok'), take(1)).subscribe(() => this.pedir());
  }

  reintentar(): void {
    if (this.estado() !== 'cargando') this.pedir();
  }

  cambiarA(org: OrganizacionDelUsuario): void {
    if (org.current || this.cambiandoA()) return;
    this.cambiandoA.set(org);
    void this.auth.cambiarDeOrganizacion(org.id).finally(() => this.cambiandoA.set(null));
  }

  /** Sin lista no hay `org_…` que pasar: que elija en el selector de Auth0, que es el
   *  mismo del login pero sin volver a pedir credenciales. */
  elegirEnAuth0(): void {
    if (this.cambiandoA()) return;
    this.cambiandoA.set('auth0');
    void this.auth.cambiarDeOrganizacion().finally(() => this.cambiandoA.set(null));
  }

  private pedir(): void {
    this.estado.set('cargando');
    this.gql.getMyOrganizations().subscribe({
      next: lista => {
        this.organizaciones.set(lista);
        this.estado.set('ok');
      },
      error: (error: unknown) => {
        console.warn('[Organizaciones] no se pudo cargar la lista:', error instanceof Error ? error.message : error);
        this.estado.set('error');
      },
    });
  }
}
